import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, SpinLoading, Form, Input, Toast } from 'antd-mobile'
import { validateVerificationCode } from '../utils/validation'

/**
 * 手环信息查询页面
 *
 * URL 示例: https://h.blet.cc/query/qUN6Vy3
 * braceletId 从 URL 路径中提取: "qUN6Vy3"
 *
 * 用于路人扫码查看佩戴者信息，紧急联系时使用
 * 需要先验证手环验证码，才能查看详细信息
 */

interface UserInfo {
  name: string
  age: number
  gender: string
  blood_type: string
  phone: string
  emergency_contact_name: string
  emergency_contact_phone: string
  emergency_contact_relation: string
  address?: string
  medical_history?: string
  chronic_diseases?: string
  allergies?: string
  medications?: string
  special_needs?: string
  notes?: string
}

interface BraceletInfo {
  bracelet_id: string
  status: string
  activated_at?: string
  user_info?: UserInfo
}

type ViewStep = 'verify' | 'detail'

export default function QueryPage() {
  const { braceletId } = useParams<{ braceletId: string }>()
  const [currentStep, setCurrentStep] = useState<ViewStep>('verify')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [info, setInfo] = useState<BraceletInfo | null>(null)
  const [error, setError] = useState<string>('')
  const [codeError, setCodeError] = useState<string>('')

  // 验证码实时验证
  const validateCode = (code: string) => {
    if (!code) {
      setCodeError('请输入验证码')
      return false
    }
    if (!validateVerificationCode(code.toUpperCase())) {
      setCodeError('验证码必须是6位大写字母或数字')
      return false
    }
    setCodeError('')
    return true
  }

  // 验证码验证
  const handleVerify = async () => {
    if (!validateCode(verificationCode)) {
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_code: verificationCode }),
      })

      const data = await response.json()

      if (response.ok) {
        Toast.show({ icon: 'success', content: '验证成功！' })
        // 验证成功后获取手环信息
        await fetchBraceletInfo()
      } else {
        Toast.show({
          icon: 'fail',
          content: data.detail || data.message || '验证码错误',
        })
      }
    } catch (error) {
      console.error('Verification error:', error)
      Toast.show({ icon: 'fail', content: '网络错误，请重试' })
    } finally {
      setLoading(false)
    }
  }

  const fetchBraceletInfo = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`/api/bracelets/${braceletId}`)
      const data = await response.json()

      if (response.ok) {
        setInfo(data)
        setCurrentStep('detail')
      } else {
        setError(data.detail || data.message || '查询失败')
      }
    } catch (err) {
      console.error('Query error:', err)
      setError('网络错误，请重试')
    } finally {
      setLoading(false)
    }
  }

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone}`
  }

  const handleSMS = (phone: string) => {
    window.location.href = `sms:${phone}`
  }

  // 渲染验证码验证步骤
  const renderVerifyStep = () => (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-2xl mx-auto pt-20">
        {/* 顶部Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-3">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            验证身份
          </h1>
          <p className="text-lg text-gray-600">
            为保护隐私，请先验证手环所有权
          </p>
          {/* 手环ID - 弱化显示 */}
          <div className="mt-2">
            <span className="text-xs text-gray-400">设备编号：{braceletId}</span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">输入验证码</h2>

          <Form layout="vertical">
            <Form.Item>
              <div className="mb-3">
                <label className="text-lg font-medium text-gray-700">验证码</label>
                <p className="text-base text-gray-500 mt-1">请输入手环背面的 6 位验证码</p>
              </div>
              <Input
                placeholder="请输入验证码"
                maxLength={6}
                value={verificationCode}
                onChange={(val) => {
                  setVerificationCode(val.toUpperCase())
                  validateCode(val)
                }}
                onBlur={() => validateCode(verificationCode)}
                style={{
                  '--font-size': '20px',
                  '--text-align': 'center',
                  fontSize: '20px',
                  height: '56px',
                  letterSpacing: '0.3em',
                  borderRadius: '12px',
                  borderColor: codeError ? '#ff4d4f' : undefined
                }}
              />
              {codeError && (
                <div className="text-red-500 text-sm mt-2">{codeError}</div>
              )}
            </Form.Item>

            <Button
              block
              color="primary"
              size="large"
              loading={loading}
              onClick={handleVerify}
              style={{
                height: '56px',
                fontSize: '18px',
                fontWeight: 'bold',
                marginTop: '24px',
                borderRadius: '12px'
              }}
            >
              验证并查看
            </Button>

            <div className="text-center mt-4 text-xs text-gray-500">
              点击"验证并查看"即表示您已阅读并同意
              <a href="/tos-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 mx-1">服务条款</a>
              和
              <a href="/privacypolicy-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 mx-1">隐私政策</a>
            </div>
          </Form>

          <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-bold text-blue-900 mb-3 text-lg">隐私保护提醒</h3>
            <ul className="space-y-2 text-base text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>验证码在手环背面，共 6 位字符</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>验证通过后可查看紧急联系信息</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>请仅在紧急情况下使用这些信息</span>
              </li>
            </ul>
          </div>

          <div className="text-center mt-6">
            <Button
              color="default"
              onClick={() => { window.location.href = '/' }}
              style={{
                height: '48px',
                fontSize: '17px',
                borderRadius: '10px',
                paddingLeft: '32px',
                paddingRight: '32px'
              }}
            >
              返回首页
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  // 加载中
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <SpinLoading style={{ fontSize: 48, color: '#1677ff' }} />
          <p className="mt-4 text-lg text-gray-600">
            {currentStep === 'verify' ? '正在验证...' : '正在查询手环信息...'}
          </p>
        </div>
      </div>
    )
  }

  // 如果还在验证步骤，显示验证界面
  if (currentStep === 'verify') {
    return renderVerifyStep()
  }

  // 错误状态
  if (error || !info) {
    return (
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-2xl mx-auto pt-20">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">查询失败</h3>
            <p className="text-lg text-gray-600 mb-8">
              {error || '未找到手环信息，请检查手环编号是否正确'}
            </p>
            <Button
              block
              color="primary"
              onClick={() => { window.location.href = '/' }}
              style={{
                height: '52px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '12px'
              }}
            >
              返回首页
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // 未激活状态
  if (info.status !== 'active') {
    return (
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-2xl mx-auto pt-20">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
              <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">手环未激活</h3>
            <p className="text-lg text-gray-600 mb-8">
              此手环尚未激活，请先完成激活流程。
            </p>
            <Button
              block
              color="primary"
              onClick={() => { window.location.href = `/${braceletId}` }}
              style={{
                height: '52px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '12px'
              }}
            >
              前往激活
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // 确保有用户信息
  if (!info.user_info) {
    return (
      <div className="min-h-screen bg-white p-4">
        <div className="max-w-2xl mx-auto pt-20">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">暂无信息</h3>
            <p className="text-lg text-gray-600 mb-8">
              手环已激活，但暂无用户信息。
            </p>
            <Button
              block
              color="primary"
              onClick={() => { window.location.href = '/' }}
              style={{
                height: '52px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '12px'
              }}
            >
              返回首页
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // 正常显示信息
  const userInfo = info.user_info

  return (
    <div className="min-h-screen bg-white p-4 pb-20">
      <div className="max-w-2xl mx-auto pt-6">
        {/* 顶部Logo和标题 */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-3">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            紧急联系信息
          </h1>
          <p className="text-lg text-gray-600">
            如需帮助，请联系下方联系人
          </p>
          {/* 手环ID - 弱化显示 */}
          <div className="mt-3">
            <span className="text-xs text-gray-400">设备编号：{info.bracelet_id}</span>
          </div>
        </div>

        {/* 佩戴者基本信息 */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-4">
          <h3 className="text-xl font-bold text-gray-900 mb-4">佩戴者信息</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">姓名</span>
              <span className="text-lg font-semibold text-gray-900">{userInfo.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">年龄</span>
              <span className="text-lg font-semibold text-gray-900">{userInfo.age} 岁</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">性别</span>
              <span className="text-lg font-semibold text-gray-900">{userInfo.gender}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">血型</span>
              <span className="text-lg font-semibold text-gray-900">{userInfo.blood_type}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-base text-gray-600">联系电话</span>
              <span className="text-lg font-semibold text-gray-900">{userInfo.phone}</span>
            </div>
            {userInfo.address && (
              <div className="pt-2 border-t border-gray-200">
                <div className="text-base text-gray-600 mb-1">居住地址</div>
                <div className="text-base font-medium text-gray-900">{userInfo.address}</div>
              </div>
            )}
          </div>
        </div>

        {/* 紧急联系人 - 突出显示 */}
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-4">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-3">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-red-700">
              紧急联系人
            </h3>
          </div>

          <div className="space-y-3 mb-5">
            <div className="flex justify-between items-center">
              <span className="text-lg text-gray-700">姓名</span>
              <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-lg text-gray-700">关系</span>
              <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_relation}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-lg text-gray-700">电话</span>
              <span className="text-xl font-bold text-gray-900">{userInfo.emergency_contact_phone}</span>
            </div>
          </div>

          {/* 快捷操作按钮 */}
          <div className="flex gap-3">
            <Button
              block
              color="danger"
              size="large"
              onClick={() => handleCall(userInfo.emergency_contact_phone)}
              style={{
                flex: 1,
                height: '56px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '12px'
              }}
            >
              📞 拨打电话
            </Button>
            <Button
              block
              color="primary"
              size="large"
              onClick={() => handleSMS(userInfo.emergency_contact_phone)}
              style={{
                flex: 1,
                height: '56px',
                fontSize: '18px',
                fontWeight: 'bold',
                borderRadius: '12px'
              }}
            >
              💬 发送短信
            </Button>
          </div>
        </div>

        {/* 医疗信息 - 始终显示 */}
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-4">
          <div className="flex items-center mb-4">
            <div className="inline-flex items-center justify-center w-10 h-10 bg-yellow-500 rounded-full mr-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-yellow-900">重要医疗信息</h3>
          </div>

          <div className="space-y-4">
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">病史</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.medical_history ? 'text-red-700' : 'text-gray-400'}`}>
                  {userInfo.medical_history || '未填写'}
                </p>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">慢性疾病 / 既往病史</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.chronic_diseases ? 'text-red-700' : 'text-gray-400'}`}>
                  {userInfo.chronic_diseases || '未填写'}
                </p>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">药物过敏史</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.allergies ? 'text-red-700' : 'text-gray-400'}`}>
                  {userInfo.allergies || '未填写'}
                </p>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">正在服用的药物</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.medications ? 'text-blue-700' : 'text-gray-400'}`}>
                  {userInfo.medications || '未填写'}
                </p>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">特殊需求</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.special_needs ? 'text-gray-700' : 'text-gray-400'}`}>
                  {userInfo.special_needs || '未填写'}
                </p>
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-gray-700 mb-2">备注</div>
              <div className="bg-white border border-yellow-200 rounded-lg p-3">
                <p className={`text-base leading-relaxed ${userInfo.notes ? 'text-gray-700' : 'text-gray-400'}`}>
                  {userInfo.notes || '未填写'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 温馨提示 */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
          <div className="flex items-center mb-3">
            <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full mr-3">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-blue-900">温馨提示</h4>
          </div>
          <ul className="space-y-2 text-base text-blue-800">
            <li className="flex items-start">
              <span className="text-blue-500 mr-2 mt-1">•</span>
              <span className="leading-relaxed">如发现佩戴者需要帮助，请立即联系紧急联系人</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2 mt-1">•</span>
              <span className="leading-relaxed">如遇紧急医疗情况，请先拨打 120 急救电话</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-500 mr-2 mt-1">•</span>
              <span className="leading-relaxed">请注意佩戴者的过敏史和用药情况，告知医护人员</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
